<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="max-w-7xl mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('AI agent') }}: {{ selectedAiFunctionName }}</h1>
                        <p class="mt-1 text-sm text-gray-500">Key: {{ selected_ai_function_key }}</p>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings/ai">
                            {{ $t('Back') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div class="xl:col-span-8 space-y-6">
                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Activation') }}</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Enables or disables this AI agent globally.') }}
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_enabled">{{ $t('Agent enabled') }}</label>
                                        <input-switch
                                            id="selected_ai_function_enabled"
                                            v-model="selectedAiFunctionEnabled"
                                            :disabled-label="$t('Agent is disabled')"
                                            :enabled-label="$t('Agent is enabled')"
                                        ></input-switch>
                                    </div>

                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_model_override">{{ $t('Agent model (optional)') }}</label>
                                        <div class="relative">
                                            <select
                                                id="selected_ai_function_model_override"
                                                v-model="selectedAiFunctionModelOverride"
                                                class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                            >
                                                <option value="">{{ $t('Use global model') }}</option>
                                                <option value="gpt-4.1-mini">gpt-4.1-mini ({{ $t('faster') }})</option>
                                                <option value="gpt-5-mini">gpt-5-mini</option>
                                                <option value="gpt-5">gpt-5</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label class="stage-filter-label block mb-2" for="selected_ai_function_max_output_tokens">Max Output Tokens</label>
                                            <div class="relative">
                                                <select
                                                    id="selected_ai_function_max_output_tokens"
                                                    v-model.number="selectedAiFunctionMaxOutputTokens"
                                                    class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                                >
                                                    <option :value="120">120 ({{ $t('very short') }})</option>
                                                    <option :value="180">180 ({{ $t('fast') }})</option>
                                                    <option :value="220">220</option>
                                                    <option :value="300">300</option>
                                                    <option :value="500">500</option>
                                                    <option :value="750">750</option>
                                                    <option :value="1000">1000</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label class="stage-filter-label block mb-2" for="selected_ai_function_temperature">Temperature</label>
                                            <div class="relative">
                                                <select
                                                    id="selected_ai_function_temperature"
                                                    v-model.number="selectedAiFunctionTemperature"
                                                    class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                                >
                                                    <option :value="0">0.0 ({{ $t('very stable') }})</option>
                                                    <option :value="0.1">0.1</option>
                                                    <option :value="0.2">0.2</option>
                                                    <option :value="0.3">0.3</option>
                                                    <option :value="0.5">0.5</option>
                                                    <option :value="0.7">0.7</option>
                                                    <option :value="1">1.0</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Function context and action instructions') }}</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Defines the usage context and the professional instruction for the response.') }}
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_context_hint">{{ $t('Agent usage context') }}</label>
                                        <div class="relative">
                                            <textarea
                                                id="selected_ai_function_context_hint"
                                                v-model="selectedAiFunctionContextHint"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                                maxlength="500"
                                                :placeholder="$t('Short note describing the professional context in which the agent operates.')"
                                                rows="2"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_prompt">{{ $t('Output instruction (prompt template)') }}</label>
                                        <div class="relative">
                                            <textarea
                                                id="selected_ai_function_prompt"
                                                v-model="selectedAiFunctionPrompt"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                                maxlength="10000"
                                                rows="10"
                                                style="height: 300px;"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Extended schema rules') }}</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Extends the fixed global response schema with agent-specific rules.') }}
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                                    <div>
                                        <label class="stage-filter-label block mb-2" for="global_schema_rules_readonly">{{ $t('Global schema rules (fixed)') }}</label>
                                        <div class="relative">
                                            <textarea
                                                id="global_schema_rules_readonly"
                                                :value="globalSchemaRulesText"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5 bg-gray-100 text-gray-500"
                                                readonly
                                                rows="6"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_schema_rules_append">{{ $t('Optional additions') }}</label>
                                        <div class="relative">
                                            <textarea
                                                id="selected_ai_function_schema_rules_append"
                                                v-model="selectedAiFunctionSchemaRulesAppend"
                                                class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                                maxlength="5000"
                                                rows="5"
                                                style="height: 300px;"
                                                :placeholder="$t('Optional: Additional rules for this agent only.')"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="bg-white shadow sm:rounded overflow-hidden">
                            <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900">Cache</h3>
                                    <div class="mt-2 text-sm text-gray-500 leading-5">
                                        {{ $t('Controls reuse when prompt fingerprint and ticket input are identical.') }}
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2 space-y-4">
                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_cache_enabled">{{ $t('Cache enabled') }}</label>
                                        <input-switch
                                            id="selected_ai_function_cache_enabled"
                                            v-model="selectedAiFunctionCacheEnabled"
                                            :disabled-label="$t('Cache is disabled')"
                                            :enabled-label="$t('Cache is enabled')"
                                        ></input-switch>
                                    </div>

                                    <div>
                                        <label class="stage-filter-label block mb-2" for="selected_ai_function_cache_ttl">{{ $t('Cache TTL (minutes)') }}</label>
                                        <div class="relative">
                                            <select
                                                id="selected_ai_function_cache_ttl"
                                                v-model="selectedAiFunctionCacheTtlMinutes"
                                                class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                            >
                                                <option value="unlimited">{{ $t('Unlimited') }}</option>
                                                <option :value="60">60 (1 {{ $t('hour') }})</option>
                                                <option :value="360">360 (6 {{ $t('hours') }})</option>
                                                <option :value="1440">1440 (1 {{ $t('day') }})</option>
                                                <option :value="10080">10080 (7 {{ $t('days') }})</option>
                                                <option :value="43200">43200 (30 {{ $t('days') }})</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                                <button
                                    :disabled="cacheClearLoading || !selected_ai_function_key"
                                    class="btn-stage-secondary"
                                    type="button"
                                    @click="clearAgentCache"
                                >
                                    {{ cacheClearLoading ? $t('Deleting cache...') : $t('Delete cache') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="xl:col-span-4 space-y-6">
                        <div class="bg-white shadow sm:rounded">
                            <div class="p-6 space-y-4">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('AI agent sandbox') }}</h3>
                                <p class="text-sm text-gray-500 leading-5">
                                    {{ $t('Test flow in clear steps: load input, validate prompt, run AI and compare results.') }}
                                </p>

                                <div class="pt-1">
                                    <label class="stage-filter-label block mb-2" for="prompt_test_input_value">{{ sandboxInputLabel }}</label>
                                    <div class="flex items-end gap-2">
                                        <input
                                            id="prompt_test_input_value"
                                            v-model.number="prompt_test_input_value"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                            min="1"
                                            step="1"
                                            type="number"
                                        >
                                        <button :disabled="prompt_test_ticket_loading || !isSandboxSupported" class="btn-stage-secondary whitespace-nowrap" type="button" @click="loadPromptTestTicket">
                                            {{ sandboxLabels.loadButton }}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="sandbox_user_group">{{ $t('User group') }}</label>
                                    <div class="relative">
                                        <select
                                            id="sandbox_user_group"
                                            v-model="prompt_test_user_group"
                                            class="stage-filter-input form-select block w-full pr-10 text-sm transition duration-150 ease-in-out sm:leading-5"
                                        >
                                            <option
                                                v-for="group in sandboxPromptGroups"
                                                :key="'sandbox-group-' + group.key"
                                                :value="group.key"
                                            >
                                                {{ group.label }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <p v-if="!isSandboxSupported" class="text-xs text-amber-700">
                                    {{ $t('No sandbox blueprint exists for this AI agent yet.') }}
                                </p>

                                <div class="text-xs text-gray-500">
                                    {{ $t('Status') }}:
                                    <span v-if="prompt_test_payload_loaded" class="text-green-600">{{ $t('Input loaded') }}</span>
                                    <span v-else>{{ $t('No input loaded yet') }}</span>
                                </div>

                                <div class="border-t border-gray-200 pt-3">
                                    <label class="stage-filter-label block mb-2">{{ $t('Build prompt string:') }}</label>
                                </div>
                                <div class="flex items-center gap-2">
                                    <button :disabled="!prompt_test_payload_loaded || !isSandboxSupported" class="w-full px-4 py-2 text-sm font-semibold rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" type="button" @click="renderPromptTest">
                                        {{ sandboxLabels.promptButton }}
                                    </button>
                                </div>

                                <p v-if="prompt_test_error" class="text-sm text-red-600">
                                    {{ prompt_test_error }}
                                </p>

                                <div class="border border-gray-300 rounded overflow-hidden bg-white">
                                    <button
                                        :disabled="!prompt_test_payload_loaded"
                                        class="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center justify-between"
                                        type="button"
                                        @click="togglePromptTestPanel('input')"
                                    >
                                        <span>{{ sandboxLabels.inputPanel }}</span>
                                        <span class="text-xs text-gray-500">{{ prompt_test_open_panels.input ? '▾' : '▸' }}</span>
                                    </button>
                                    <div v-if="prompt_test_open_panels.input" class="p-2 border-t border-gray-200">
                                        <code-viewer :code="prompt_test_input_json" language="json"></code-viewer>
                                    </div>
                                </div>

                                <div class="border border-gray-300 rounded overflow-hidden bg-white">
                                    <button
                                        :disabled="!prompt_test_payload_loaded"
                                        class="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center justify-between"
                                        type="button"
                                        @click="togglePromptTestPanel('pseudonymized')"
                                    >
                                        <span>{{ sandboxLabels.pseudonymizedPanel }}</span>
                                        <span class="text-xs text-gray-500">{{ prompt_test_open_panels.pseudonymized ? '▾' : '▸' }}</span>
                                    </button>
                                    <div v-if="prompt_test_open_panels.pseudonymized" class="p-2 border-t border-gray-200">
                                        <code-viewer :code="prompt_test_pseudonymized_json" language="json"></code-viewer>
                                    </div>
                                </div>

                                <div class="border border-gray-300 rounded overflow-hidden bg-white">
                                    <button
                                        :disabled="!prompt_test_rendered"
                                        class="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center justify-between"
                                        type="button"
                                        @click="togglePromptTestPanel('rendered')"
                                    >
                                        <span>{{ sandboxLabels.renderedPanel }}</span>
                                        <span class="text-xs text-gray-500">{{ prompt_test_open_panels.rendered ? '▾' : '▸' }}</span>
                                    </button>
                                    <div v-if="prompt_test_open_panels.rendered" class="p-2 border-t border-gray-200">
                                        <code-viewer :code="prompt_test_rendered" language="text"></code-viewer>
                                    </div>
                                </div>

                                <div class="border-t border-gray-200 pt-3">
                                    <label class="stage-filter-label block mb-2">{{ $t('AI API call:') }}</label>
                                </div>
                                <div class="flex items-center gap-2">
                                    <button :disabled="!prompt_test_payload_loaded || prompt_test_ai_loading || !isSandboxSupported" class="w-full px-4 py-2 text-sm font-semibold rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" type="button" @click="runAiTest">
                                        {{ sandboxLabels.runButton }}
                                    </button>
                                </div>

                                <div class="border-t border-gray-200 pt-3">
                                    <label class="stage-filter-label block mb-2">{{ $t('AI API response:') }}</label>
                                </div>
                                <div class="border border-gray-300 rounded overflow-hidden bg-white">
                                    <button
                                        :disabled="!prompt_test_ai_result_raw_json"
                                        class="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center justify-between"
                                        type="button"
                                        @click="togglePromptTestPanel('aiResultRaw')"
                                    >
                                        <span>{{ sandboxLabels.rawResultPanel }}</span>
                                        <span class="text-xs text-gray-500">{{ prompt_test_open_panels.aiResultRaw ? '▾' : '▸' }}</span>
                                    </button>
                                    <div v-if="prompt_test_open_panels.aiResultRaw" class="p-2 border-t border-gray-200">
                                        <code-viewer :code="prompt_test_ai_result_raw_json" language="json"></code-viewer>
                                    </div>
                                </div>

                                <div class="border border-gray-300 rounded overflow-hidden bg-white">
                                    <button
                                        :disabled="!prompt_test_ai_result_json"
                                        class="w-full text-left px-3 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center justify-between"
                                        type="button"
                                        @click="togglePromptTestPanel('aiResult')"
                                    >
                                        <span>{{ sandboxLabels.finalResultPanel }}</span>
                                        <span class="text-xs text-gray-500">{{ prompt_test_open_panels.aiResult ? '▾' : '▸' }}</span>
                                    </button>
                                    <div v-if="prompt_test_open_panels.aiResult" class="p-2 border-t border-gray-200">
                                        <code-viewer :code="prompt_test_ai_result_json" language="json"></code-viewer>
                                    </div>
                                </div>

                                <div>
                                    <label class="stage-filter-label block mb-2" for="prompt_test_latency_ms">{{ $t('AI latency (ms)') }}</label>
                                    <input
                                        id="prompt_test_latency_ms"
                                        :value="prompt_test_latency_ms !== null ? prompt_test_latency_ms : ''"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        readonly
                                        type="text"
                                    >
                                </div>

                                <div>
                                    <label class="stage-filter-label block mb-2" for="prompt_test_fingerprint">{{ $t('Prompt fingerprint (cache key)') }}</label>
                                    <input
                                        id="prompt_test_fingerprint"
                                        :value="prompt_test_fingerprint"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        readonly
                                        type="text"
                                    >
                                    <p class="mt-2 text-xs text-gray-500 leading-5">
                                        {{ $t('The fingerprint is the technical checksum for the cache key.') }}
                                        {{ $t('Changing prompt or ticket input automatically creates a new cache entry.') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
import CodeViewer from "@/components/ui/code-viewer";
import { AGENT_SANDBOX_BLUEPRINTS } from "@/views/pages/workspace/admin/settings/agent-sandbox-blueprints";

export default {
    name: 'settings-ai-agent-ticket-summary',
    components: {
        CodeViewer,
    },
    metaInfo() {
        return {
            title: this.$i18n.t('AI agent settings')
        };
    },
    computed: {
        selectedAiFunctionMeta() {
            return (this.ai_function_catalog || []).find((item) => String(item.key) === String(this.selected_ai_function_key)) || null;
        },
        selectedAiFunctionName() {
            return this.selectedAiFunctionMeta ? String(this.selectedAiFunctionMeta.name || this.selected_ai_function_key) : String(this.selected_ai_function_key || '');
        },
        sandboxPromptGroups() {
            return [
                { key: 'employee', label: this.$t('Employees') },
                { key: 'customer', label: this.$t('Customer') },
                { key: 'guest', label: this.$t('Guest') },
            ];
        },
        selectedSandboxPromptGroup() {
            const selected = String(this.prompt_test_user_group || '');
            return this.sandboxPromptGroups.find((group) => group.key === selected) || this.sandboxPromptGroups[0];
        },
        selectedSandboxGroupPrompts() {
            const key = this.selectedSandboxPromptGroup ? this.selectedSandboxPromptGroup.key : 'employee';
            const promptGroups = this.ai_prompts_by_group && typeof this.ai_prompts_by_group === 'object'
                ? this.ai_prompts_by_group
                : {};
            const selected = promptGroups[key];
            return selected && typeof selected === 'object'
                ? selected
                : { system_prompt: '', safety_prompt: '' };
        },
        selectedAiFunctionDescription() {
            return this.selectedAiFunctionMeta ? String(this.selectedAiFunctionMeta.description || '') : '';
        },
        currentAiFunctionConfig() {
            const meta = this.selectedAiFunctionMeta;
            const defaults = meta && meta.defaults && typeof meta.defaults === 'object'
                ? meta.defaults
                : {};
            const current = this.ai_functions_config && typeof this.ai_functions_config === 'object'
                ? this.ai_functions_config[this.selected_ai_function_key]
                : null;

            if (!current || typeof current !== 'object') {
                return Object.assign({}, defaults);
            }

            return Object.assign({}, defaults, current);
        },
        selectedAiFunctionPrompt: {
            get() {
                return this.currentAiFunctionConfig.prompt_template || '';
            },
            set(value) {
                this.setSelectedAiFunctionField('prompt_template', value);
            }
        },
        selectedAiFunctionEnabled: {
            get() {
                return Boolean(this.currentAiFunctionConfig.enabled);
            },
            set(value) {
                this.setSelectedAiFunctionField('enabled', Boolean(value));
            }
        },
        selectedAiFunctionModelOverride: {
            get() {
                return this.currentAiFunctionConfig.model_override || '';
            },
            set(value) {
                this.setSelectedAiFunctionField('model_override', value === '' ? null : String(value));
            }
        },
        selectedAiFunctionMaxOutputTokens: {
            get() {
                const value = Number(this.currentAiFunctionConfig.max_output_tokens || 500);
                return Math.min(4000, Math.max(50, value));
            },
            set(value) {
                const tokens = Number(value || 500);
                this.setSelectedAiFunctionField('max_output_tokens', Math.min(4000, Math.max(50, tokens)));
            }
        },
        selectedAiFunctionTemperature: {
            get() {
                const value = Number(this.currentAiFunctionConfig.temperature);
                if (!Number.isFinite(value)) {
                    return 0;
                }
                return Math.min(2, Math.max(0, value));
            },
            set(value) {
                const temperature = Number(value);
                this.setSelectedAiFunctionField('temperature', Number.isFinite(temperature)
                    ? Math.min(2, Math.max(0, temperature))
                    : 0);
            }
        },
        selectedAiFunctionCacheEnabled: {
            get() {
                return Boolean(this.currentAiFunctionConfig.cache_enabled);
            },
            set(value) {
                this.setSelectedAiFunctionField('cache_enabled', Boolean(value));
            }
        },
        selectedAiFunctionContextHint: {
            get() {
                return this.currentAiFunctionConfig.context_hint || '';
            },
            set(value) {
                this.setSelectedAiFunctionField('context_hint', value);
            }
        },
        selectedAiFunctionSchemaRulesAppend: {
            get() {
                return this.currentAiFunctionConfig.schema_rules_append || '';
            },
            set(value) {
                this.setSelectedAiFunctionField('schema_rules_append', value);
            }
        },
        selectedAiFunctionCacheTtlMinutes: {
            get() {
                if (this.currentAiFunctionConfig.cache_ttl_minutes === null) {
                    return 'unlimited';
                }
                return String(Number(this.currentAiFunctionConfig.cache_ttl_minutes || 10080));
            },
            set(value) {
                if (value === 'unlimited') {
                    this.setSelectedAiFunctionField('cache_ttl_minutes', null);
                    return;
                }
                const ttl = Number(value || 10080);
                this.setSelectedAiFunctionField('cache_ttl_minutes', Math.min(43200, Math.max(1, ttl)));
            }
        },
        globalSchemaRulesText() {
            return [
                '1. "reply" is the main text of your response',
                '2. "actions", "alerts", "warnings" and "notes" are arrays of short strings',
                '3. "links" is an array of objects with label and url.',
                '4. Empty fields are returned as empty arrays',
                '5. No additional fields are returned',
            ].join('\n');
        },
        promptTemplatePreviewWithPlaceholders() {
            const selectedKey = String(this.selected_ai_function_key || '');
            const outputSchema = this.selectedAiFunctionMeta && this.selectedAiFunctionMeta.output_schema
                ? JSON.stringify(this.selectedAiFunctionMeta.output_schema, null, 2)
                : '{}';
            const isEnglish = this.ai_default_locale === 'en';
            const responseLanguage = isEnglish ? 'English' : 'Deutsch';

            if (selectedKey === 'ticket_summary') {
                const defaultSummaryPrompt = 'Create a complete and concise summary of the entire ticket history in 5 to 8 short sentences. '
                    + 'Important constraints, decisions and open points must not be omitted. '
                    + 'If open tasks exist, list them as concrete next steps in actions. '
                    + 'Use alerts for urgent or time-critical points. '
                    + 'Use warnings for risks, blockers or missing data. '
                    + 'Use notes for neutral, helpful context without immediate action pressure. '
                    + 'Use links only for links present in the payload and return links as an object list with label and url.';
                const summaryGuidance = (this.currentAiFunctionConfig.prompt_template || '').trim() !== ''
                    ? String(this.currentAiFunctionConfig.prompt_template).trim()
                    : defaultSummaryPrompt;
                const contextHint = (this.currentAiFunctionConfig.context_hint || '').trim();
                const schemaRulesAppend = (this.currentAiFunctionConfig.schema_rules_append || '').trim();

                return [
                    '### SYSTEM-PROMPT',
                    '',
                    isEnglish ? 'Role: Assistant for ticket summaries' : 'Rolle: Assistent für Ticket-Zusammenfassungen',
                    isEnglish ? 'Source: Facts from the provided JSON payload only' : 'Quelle: Ausschließlich Fakten aus dem bereitgestellten JSON-Payload',
                    '',
                    isEnglish ? 'Fixed response schema:' : 'Festes Antwort-Schema:',
                    outputSchema,
                    '',
                    isEnglish ? 'Fixed rules:' : 'Feste Regeln:',
                    isEnglish ? '1. "reply" is the main text of your response' : '1. "reply" ist der Haupttext deiner Antwort',
                    isEnglish ? '2. "actions", "alerts", "warnings" and "notes" are arrays of short strings' : '2. "actions", "alerts", "warnings" und "notes" sind Arrays aus kurzen Strings',
                    isEnglish ? '3. "links" is an array of objects with label and url.' : '3. "links" ist ein Array aus Objekten mit label und url.',
                    isEnglish ? '4. Empty fields are returned as empty arrays' : '4. Felder ohne Inhalt werden als leere Arrays zurückgegeben',
                    isEnglish ? '5. No additional fields are returned' : '5. Es werden keine zusätzlichen Felder zurückgegeben',
                    isEnglish ? '6. All string values in ' + responseLanguage : '6. Alle String-Werte in ' + responseLanguage,
                    '',
                    isEnglish ? 'Function context:' : 'Funktionskontext:',
                    contextHint !== '' ? contextHint : '[nicht gesetzt]',
                    '',
                    isEnglish ? 'Data protection:' : 'Datenschutz:',
                    this.ai_pseudonymization_enabled
                        ? (isEnglish ? 'Pseudonymization active (names are replaced by aliases before the AI request).' : 'Pseudonymisierung aktiv (Namen werden vor der KI-Anfrage durch Aliaswerte ersetzt).')
                        : (isEnglish ? 'Pseudonymization disabled (clear names are in the payload).' : 'Pseudonymisierung deaktiviert (Klarnamen sind im Payload enthalten).'),
                    '',
                    isEnglish ? 'Agent action guidance:' : 'Ausgabeanweisung des Agenten:',
                    summaryGuidance,
                    '',
                    isEnglish ? 'Extended schema rules:' : 'Erweiterte Schema-Regeln:',
                    schemaRulesAppend !== '' ? schemaRulesAppend : '[keine]',
                    '',
                    isEnglish ? 'User-group system prompt: {{ROLE_SYSTEM_PROMPT}}' : 'Benutzergruppen-System-Prompt: {{ROLE_SYSTEM_PROMPT}}',
                    isEnglish ? 'User-group safety prompt: {{ROLE_SAFETY_PROMPT}}' : 'Benutzergruppen-Safety-Prompt: {{ROLE_SAFETY_PROMPT}}',
                    '',
                    isEnglish ? '### USER-PROMPT' : '### NUTZER-PROMPT',
                    isEnglish ? 'Ticket payload (JSON):' : 'Ticket-Payload (JSON):',
                    '{{TICKET_JSON}}',
                ].join('\n');
            }

            return [
                '### SYSTEM-PROMPT',
                (this.currentAiFunctionConfig.prompt_template || '').trim(),
                '',
                isEnglish ? '### USER-PROMPT' : '### NUTZER-PROMPT',
                '{{FUNCTION_INPUT_JSON}}',
            ].join('\n');
        },
        activeSandboxBlueprint() {
            const sandboxMeta = this.selectedAiFunctionMeta && typeof this.selectedAiFunctionMeta.sandbox === 'object'
                ? this.selectedAiFunctionMeta.sandbox
                : {};
            const key = String(sandboxMeta.type || this.selected_ai_function_key || '');
            if (Object.prototype.hasOwnProperty.call(AGENT_SANDBOX_BLUEPRINTS, key)) {
                return AGENT_SANDBOX_BLUEPRINTS[key];
            }

            return null;
        },
        isSandboxSupported() {
            return this.activeSandboxBlueprint !== null;
        },
        sandboxInputLabel() {
            const sandboxMeta = this.selectedAiFunctionMeta && typeof this.selectedAiFunctionMeta.sandbox === 'object'
                ? this.selectedAiFunctionMeta.sandbox
                : {};

            if (sandboxMeta.input_label) {
                return String(sandboxMeta.input_label);
            }

            return this.activeSandboxBlueprint
                ? String(this.activeSandboxBlueprint.input?.label || 'Test-Input')
                : 'Test-Input';
        },
        sandboxLabels() {
            const labels = this.activeSandboxBlueprint && this.activeSandboxBlueprint.labels
                ? this.activeSandboxBlueprint.labels
                : {};

            return {
                loadButton: labels.loadButton || 'Load',
                promptButton: labels.promptButton || 'Build prompt',
                runButton: labels.runButton || 'Run',
                inputPanel: labels.inputPanel || 'Show input',
                pseudonymizedPanel: labels.pseudonymizedPanel || 'Show pseudonymized input',
                renderedPanel: labels.renderedPanel || 'Gerenderten Prompt anzeigen',
                rawResultPanel: labels.rawResultPanel || 'Show raw response',
                finalResultPanel: labels.finalResultPanel || 'Antwort anzeigen',
            };
        }
    },
    data() {
        return {
            loading: true,
            ai_enabled: false,
            ai_provider: 'openai',
            ai_default_locale: 'de',
            ai_openai_api_key: '',
            ai_openai_base_url: 'https://api.openai.com/v1',
            ai_timeout_seconds: 20,
            ai_retry_count: 1,
            ai_model_text: 'gpt-5-mini',
            ai_model_embedding: 'text-embedding-3-small',
            ai_pseudonymization_enabled: false,
            ai_prompt_groups_catalog: {},
            ai_prompts_by_group: {},
            ai_function_catalog: [],
            ai_functions_config: {},
            ai_monthly_budget: 50,
            ai_budget_currency: 'EUR',
            ai_budget_alert_percent: 80,
            ai_hard_stop_enabled: true,
            ai_max_tokens_per_request: 8000,
            selected_ai_function_key: null,
            prompt_test_input_value: null,
            prompt_test_ticket_loading: false,
            prompt_test_payload_loaded: false,
            prompt_test_input_json: '{}',
            prompt_test_pseudonymized_json: '{}',
            prompt_test_rendered: '',
            prompt_test_user_group: 'employee',
            prompt_test_error: null,
            prompt_test_fingerprint: '',
            prompt_test_latency_ms: null,
            prompt_test_ai_loading: false,
            prompt_test_ai_result_raw_json: '',
            prompt_test_ai_result_json: '',
            prompt_test_open_panels: {
                input: false,
                pseudonymized: false,
                rendered: false,
                aiResultRaw: false,
                aiResult: false,
            },
            cacheClearLoading: false,
        };
    },
    mounted() {
        this.get();
    },
    methods: {
        ensureAiFunctionConfigEntry(functionKey) {
            const key = functionKey ? String(functionKey) : null;
            if (!key) {
                return;
            }
            const meta = (this.ai_function_catalog || []).find((item) => String(item.key) === key);
            if (!meta) {
                return;
            }

            const defaults = meta.defaults && typeof meta.defaults === 'object'
                ? meta.defaults
                : {};
            const existing = this.ai_functions_config && typeof this.ai_functions_config[key] === 'object'
                ? this.ai_functions_config[key]
                : {};

            const normalized = Object.assign({}, defaults, existing);
            normalized.enabled = Boolean(normalized.enabled);
            normalized.model_override = normalized.model_override ? String(normalized.model_override).trim() : null;
            normalized.max_output_tokens = Math.min(4000, Math.max(50, Number(normalized.max_output_tokens || 500)));
            normalized.temperature = Math.min(2, Math.max(0, Number(normalized.temperature ?? 0)));
            normalized.prompt_template = normalized.prompt_template ? String(normalized.prompt_template) : null;
            normalized.context_hint = normalized.context_hint ? String(normalized.context_hint).trim() : null;
            normalized.schema_rules_append = normalized.schema_rules_append ? String(normalized.schema_rules_append).trim() : null;
            normalized.cache_enabled = Boolean(
                Object.prototype.hasOwnProperty.call(normalized, 'cache_enabled') ? normalized.cache_enabled : true
            );
            if (normalized.cache_ttl_minutes === null || normalized.cache_ttl_minutes === '' || Number(normalized.cache_ttl_minutes) <= 0) {
                normalized.cache_ttl_minutes = null;
            } else {
                normalized.cache_ttl_minutes = Math.min(43200, Math.max(1, Number(normalized.cache_ttl_minutes || 10080)));
            }

            if (!this.ai_functions_config || typeof this.ai_functions_config !== 'object') {
                this.ai_functions_config = {};
            }
            this.ai_functions_config[key] = normalized;
        },
        setSelectedAiFunctionField(field, value) {
            const key = this.selected_ai_function_key ? String(this.selected_ai_function_key) : null;
            if (!key) {
                return;
            }
            this.ensureAiFunctionConfigEntry(key);
            this.ai_functions_config[key][field] = value;
        },
        loadPromptTestTicket() {
            if (!this.isSandboxSupported) {
                this.prompt_test_error = this.$i18n.t('No sandbox blueprint exists for this AI agent yet.').toString();
                return;
            }

            this.prompt_test_error = null;
            this.prompt_test_payload_loaded = false;
            this.prompt_test_rendered = '';
            this.prompt_test_pseudonymized_json = '{}';
            this.prompt_test_fingerprint = '';
            this.prompt_test_latency_ms = null;
            this.prompt_test_ai_result_raw_json = '';
            this.prompt_test_ai_result_json = '';
            this.prompt_test_open_panels.input = false;
            this.prompt_test_open_panels.pseudonymized = false;
            this.prompt_test_open_panels.rendered = false;
            this.prompt_test_open_panels.aiResultRaw = false;
            this.prompt_test_open_panels.aiResult = false;

            const inputValue = Number(this.prompt_test_input_value || 0);
            if (inputValue < 1) {
                this.prompt_test_error = this.activeSandboxBlueprint
                    ? String(this.activeSandboxBlueprint.input?.invalidMessage || this.$i18n.t('Please enter a valid test value.').toString())
                    : this.$i18n.t('Please enter a valid test value.').toString();
                return;
            }

            this.prompt_test_ticket_loading = true;
            this.activeSandboxBlueprint.loadInputPayload(axios, inputValue).then((response) => {
                this.prompt_test_input_json = JSON.stringify(response.data || {}, null, 2);
                this.prompt_test_payload_loaded = true;
                this.renderPromptTest();
            }).catch((error) => {
                this.prompt_test_error = String(error?.response?.data?.message || this.$i18n.t('Ticket could not be loaded.').toString());
                this.prompt_test_input_json = '{}';
                this.prompt_test_payload_loaded = false;
            }).finally(() => {
                this.prompt_test_ticket_loading = false;
            });
        },
        renderPromptTest() {
            this.prompt_test_error = null;
            let sourcePayload = {};

            try {
                sourcePayload = JSON.parse(this.prompt_test_input_json || '{}');
            } catch (error) {
                this.prompt_test_error = this.$i18n.t('Loaded input is not valid JSON.').toString();
                this.prompt_test_rendered = '';
                return;
            }

            const processedPayload = this.ai_pseudonymization_enabled
                ? this.pseudonymizeSandboxPayload(sourcePayload)
                : sourcePayload;
            const formattedInput = JSON.stringify(processedPayload, null, 2);
            this.prompt_test_pseudonymized_json = formattedInput;

            let rendered = this.promptTemplatePreviewWithPlaceholders;
            rendered = rendered.replace('{{TICKET_JSON}}', formattedInput);
            rendered = rendered.replace('{{FUNCTION_INPUT_JSON}}', formattedInput);
            const systemPrompt = String(this.selectedSandboxGroupPrompts.system_prompt || '').trim();
            const safetyPrompt = String(this.selectedSandboxGroupPrompts.safety_prompt || '').trim();
            rendered = rendered.replace('{{ROLE_SYSTEM_PROMPT}}', systemPrompt !== '' ? systemPrompt : '[nicht gesetzt]');
            rendered = rendered.replace('{{ROLE_SAFETY_PROMPT}}', safetyPrompt !== '' ? safetyPrompt : '[nicht gesetzt]');

            this.prompt_test_rendered = rendered;
            this.prompt_test_fingerprint = this.buildPromptFingerprint(rendered);
            this.prompt_test_open_panels.rendered = false;
        },
        togglePromptTestPanel(panelKey) {
            this.prompt_test_open_panels[panelKey] = !this.prompt_test_open_panels[panelKey];
        },
        pseudonymizeSandboxPayload(payload) {
            const clone = JSON.parse(JSON.stringify(payload || {}));
            const mapping = {};
            const counters = {
                CUSTOMER: 0,
                AGENT: 0,
                ACTOR: 0,
                USER: 0,
            };

            const addMapping = (name, prefix) => {
                const normalized = String(name || '').trim();
                if (!normalized || Object.prototype.hasOwnProperty.call(mapping, normalized)) {
                    return;
                }
                counters[prefix] = (counters[prefix] || 0) + 1;
                mapping[normalized] = prefix + '_' + counters[prefix];
            };

            addMapping(clone?.ticket?.customer_name, 'CUSTOMER');
            addMapping(clone?.ticket?.owner_agent_name, 'AGENT');
            (Array.isArray(clone?.ticket?.assigned_agent_names) ? clone.ticket.assigned_agent_names : []).forEach((name) => addMapping(name, 'AGENT'));
            (Array.isArray(clone?.ticket?.assigned_customer_names) ? clone.ticket.assigned_customer_names : []).forEach((name) => addMapping(name, 'CUSTOMER'));
            addMapping(clone?.actor?.name, 'ACTOR');
            (Array.isArray(clone?.conversation) ? clone.conversation : []).forEach((message) => {
                addMapping(message?.author_name, 'USER');
            });

            const names = Object.keys(mapping).sort((a, b) => b.length - a.length);
            const replaceRecursively = (value) => {
                if (typeof value === 'string') {
                    return names.reduce((acc, realName) => acc.split(realName).join(mapping[realName]), value);
                }
                if (Array.isArray(value)) {
                    return value.map(replaceRecursively);
                }
                if (value && typeof value === 'object') {
                    const out = {};
                    Object.keys(value).forEach((key) => {
                        out[key] = replaceRecursively(value[key]);
                    });
                    return out;
                }
                return value;
            };

            return replaceRecursively(clone);
        },
        runAiTest() {
            if (!this.isSandboxSupported) {
                this.prompt_test_error = this.$i18n.t('No sandbox blueprint exists for this AI agent yet.').toString();
                return;
            }

            this.prompt_test_error = null;
            this.prompt_test_latency_ms = null;
            this.prompt_test_ai_result_raw_json = '';
            this.prompt_test_ai_result_json = '';
            const inputValue = Number(this.prompt_test_input_value || 0);
            if (inputValue < 1) {
                this.prompt_test_error = this.activeSandboxBlueprint
                    ? String(this.activeSandboxBlueprint.input?.invalidMessage || this.$i18n.t('Please enter a valid test value.').toString())
                    : this.$i18n.t('Please enter a valid test value.').toString();
                return;
            }

            this.prompt_test_ai_loading = true;
            const sandboxGroup = this.selectedSandboxPromptGroup ? this.selectedSandboxPromptGroup.key : 'employee';
            this.activeSandboxBlueprint.runAgent(axios, inputValue, sandboxGroup).then((response) => {
                this.prompt_test_ai_result_raw_json = JSON.stringify(response.data?.raw_ai_payload || {}, null, 2);
                this.prompt_test_ai_result_json = JSON.stringify(response.data || {}, null, 2);
                const latencyValue = response.data?.latency_ms ?? response.data?.raw_ai_payload?.latency_ms ?? null;
                const latencyNumber = Number(latencyValue);
                this.prompt_test_latency_ms = Number.isFinite(latencyNumber) && latencyNumber >= 0
                    ? Math.round(latencyNumber)
                    : null;
                this.prompt_test_open_panels.aiResultRaw = true;
                this.prompt_test_open_panels.aiResult = true;
            }).catch((error) => {
                if (error?.code === 'ECONNABORTED') {
                    this.prompt_test_error = this.$i18n.t('AI test exceeded the timeout limit (60s).').toString();
                    return;
                }
                this.prompt_test_error = String(error?.response?.data?.message || this.$i18n.t('AI test could not be executed.').toString());
            }).finally(() => {
                this.prompt_test_ai_loading = false;
            });
        },
        buildPromptFingerprint(content) {
            const input = String(content || '');
            let hash = 5381;
            for (let i = 0; i < input.length; i += 1) {
                hash = ((hash << 5) + hash) + input.charCodeAt(i);
                hash &= 0xffffffff;
            }
            const unsigned = hash >>> 0;
            return 'djb2-' + unsigned.toString(16).padStart(8, '0');
        },
        resolveSelectedFunction() {
            const requestedKey = String(this.$route.params.functionKey || '');
            const exists = (this.ai_function_catalog || []).some((item) => String(item.key) === requestedKey);
            if (!exists) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('AI agent was not found.').toString(),
                    type: 'error'
                });
                this.$router.push('/workspace/admin/settings/ai');
                return;
            }

            this.selected_ai_function_key = requestedKey;
            this.ensureAiFunctionConfigEntry(requestedKey);
        },
        get() {
            const self = this;
            self.loading = true;
            return axios.get('api/workspace/admin/settings/ai').then(function (response) {
                self.ai_enabled = response.data.ai_enabled === null ? false : response.data.ai_enabled;
                self.ai_provider = response.data.ai_provider || 'openai';
                self.ai_default_locale = response.data.ai_default_locale || 'de';
                self.ai_openai_api_key = response.data.ai_openai_api_key || '';
                self.ai_openai_base_url = response.data.ai_openai_base_url || 'https://api.openai.com/v1';
                self.ai_timeout_seconds = response.data.ai_timeout_seconds === null ? 20 : Number(response.data.ai_timeout_seconds);
                self.ai_retry_count = response.data.ai_retry_count === null ? 1 : Number(response.data.ai_retry_count);
                self.ai_model_text = response.data.ai_model_text || 'gpt-5-mini';
                self.ai_model_embedding = response.data.ai_model_embedding || 'text-embedding-3-small';
                self.ai_pseudonymization_enabled = Boolean(response.data.ai_pseudonymization_enabled);
                self.ai_prompt_groups_catalog = response.data.ai_prompt_groups_catalog && typeof response.data.ai_prompt_groups_catalog === 'object'
                    ? response.data.ai_prompt_groups_catalog
                    : {};
                self.ai_prompts_by_group = response.data.ai_prompts_by_group && typeof response.data.ai_prompts_by_group === 'object'
                    ? response.data.ai_prompts_by_group
                    : {};
                self.ai_function_catalog = Array.isArray(response.data.ai_function_catalog)
                    ? response.data.ai_function_catalog
                    : [];
                self.ai_functions_config = response.data.ai_functions_config && typeof response.data.ai_functions_config === 'object'
                    ? response.data.ai_functions_config
                    : {};
                self.ai_monthly_budget = response.data.ai_monthly_budget === null ? 50 : Number(response.data.ai_monthly_budget);
                self.ai_budget_currency = response.data.ai_budget_currency || 'EUR';
                self.ai_budget_alert_percent = response.data.ai_budget_alert_percent === null ? 80 : Number(response.data.ai_budget_alert_percent);
                self.ai_hard_stop_enabled = response.data.ai_hard_stop_enabled === null ? true : response.data.ai_hard_stop_enabled;
                self.ai_max_tokens_per_request = response.data.ai_max_tokens_per_request === null ? 8000 : Number(response.data.ai_max_tokens_per_request);
                self.resolveSelectedFunction();
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/admin/settings/ai');
            });
        },
        save() {
            const self = this;
            self.loading = true;
            self.ensureAiFunctionConfigEntry(self.selected_ai_function_key);

            axios.post('api/workspace/admin/settings/ai', {
                ai_enabled: self.ai_enabled ? 1 : 0,
                ai_provider: self.ai_provider,
                ai_default_locale: self.ai_default_locale,
                ai_openai_api_key: self.ai_openai_api_key || null,
                ai_openai_base_url: self.ai_openai_base_url || null,
                ai_timeout_seconds: self.ai_timeout_seconds,
                ai_retry_count: self.ai_retry_count,
                ai_model_text: self.ai_model_text,
                ai_model_embedding: self.ai_model_embedding || null,
                ai_pseudonymization_enabled: self.ai_pseudonymization_enabled ? 1 : 0,
                ai_prompts_by_group: self.ai_prompts_by_group,
                ai_functions_config: self.ai_functions_config,
                ai_monthly_budget: self.ai_monthly_budget,
                ai_budget_currency: self.ai_budget_currency,
                ai_budget_alert_percent: self.ai_budget_alert_percent,
                ai_hard_stop_enabled: self.ai_hard_stop_enabled ? 1 : 0,
                ai_max_tokens_per_request: self.ai_max_tokens_per_request,
            }).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        clearAgentCache() {
            if (!this.selected_ai_function_key || this.cacheClearLoading) {
                return;
            }

            this.cacheClearLoading = true;
            axios.delete('api/workspace/admin/settings/ai/agents/' + encodeURIComponent(this.selected_ai_function_key) + '/cache')
                .then((response) => {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: String(response.data?.message || this.$i18n.t('Cache deleted successfully').toString()),
                        type: 'success'
                    });
                })
                .catch((error) => {
                    this.$notify({
                        title: this.$i18n.t('Error').toString(),
                        text: String(error?.response?.data?.message || this.$i18n.t('An error occurred while deleting data').toString()),
                        type: 'error'
                    });
                })
                .finally(() => {
                    this.cacheClearLoading = false;
                });
        }
    }
};
</script>
